<script>

	// environment variables
	export let data;

	// svelte
	import { tick } from 'svelte';
	import { onMount } from 'svelte';
	import SectionHead from '../components/SectionHead.svelte';
	import Button from '../components/Button.svelte';
	import Graphic from '../components/Graphic.svelte';
	import Slider from '../components/Slider.svelte';

	// v99 core
	import System from '../classes/system';
	import Utils from '../classes/utils';

	// audio
  import * as Tone from 'tone';

	// content imports
	let vectorImports, sceneImports, musicImports, soundImports, controllerImports, impulseImports;
	if(boolify(data.custom)) {
		vectorImports = import.meta.glob('../custom/vectors/*.svg', {as:'raw', eager:true});
		sceneImports = import.meta.glob('../custom/scenes/*.js', {eager:true});
		controllerImports = import.meta.glob('../custom/controllers/*.js', {eager:true});
		musicImports = import.meta.glob('../custom/audio/music/*', {eager:true});
		soundImports = import.meta.glob('../custom/audio/sounds/*', {eager:true});
	} else {
		vectorImports = import.meta.glob('../examples/vectors/*.svg', {as:'raw', eager:true});
		sceneImports = import.meta.glob('../examples/scenes/*.js', {eager:true});
		controllerImports = import.meta.glob('../examples/controllers/*.js', {eager:true});
		musicImports = import.meta.glob('../examples/audio/music/*', {eager:true});
		soundImports = import.meta.glob('../examples/audio/sounds/*', {eager:true});
	}

	impulseImports = import.meta.glob('../impulses/*', {eager:true});

	// import SVGs, convert to shape paths
	let shapes = {};
	for(let url in vectorImports) {
		let name = url.replace(/^.*[\\\/]/, '').split('.')[0];
		let p = Utils.SVGtoPaths(vectorImports[url]);
		shapes[name] = p;
	}

	// import all custom scenes
	let scenes = {};
	for(let url in sceneImports) {
		let name = url.replace(/^.*[\\\/]/, '').split('.')[0];
		let m = sceneImports[url];
		scenes[name] = m;
	}

	// store all music asset URLs with extentionless keys
	let music = {};
	for(let url in musicImports) {
		let name = url.replace(/^.*[\\\/]/, '').split('.')[0];
		let a = musicImports[url];
		music[name] = a.default;
	}

	// store all SFX asset URLs with extentionless keys
	let sounds = {};
	for(let url in soundImports) {
		let name = url.replace(/^.*[\\\/]/, '').split('.')[0];
		let a = soundImports[url];
		sounds[name] = a.default;
	}

	// store all IR asset URLs with extentionless keys
	let impulses = {};
	for(let url in impulseImports) {
		let name = url.replace(/^.*[\\\/]/, '').split('.')[0];
		let i = impulseImports[url];
		impulses[name] = i.default;
	}

	// import config/gamestate controller named in .env file
	let controller;
	for(let url in controllerImports) {
		let name = url.replace(/^.*[\\\/]/, '').split('.')[0];
		if(name == data.controller) {
			controller = controllerImports[url];
		}
	}

	let elem, system;
	let await_user = boolify(data.await_user); // audio-safe click-to-start
	let debug = boolify(data.debug);
	let configOpen = false;
	let chladniConfigOpen = false;
	let settings = null;
	let dumpElem = null;
	let dump = '';

	// entry point
	onMount(() => {
		if(!await_user) initV99();
	});

	async function initV99() {
		if(await_user) {
			await Tone.start();
			await_user = false;
		}
		await tick(); // wait for container element to be created
		system = new System({
			elem: elem,
			debug: debug,
			controller: controller,
			music: music,
			sounds: sounds,
			impulses: impulses,
			scenes: scenes,
			shapes: shapes,
		});
		system.init();
		// link up display settings with debug config UI
		if(debug) settings = system.display.settings;
	}

	function boolify(string) {
		return string == 'true' ? true : false;
	}

	// reactive block
	$: {
		if(system && debug) {
			system.display.initShaders(settings); // update display settings
		}
	}

</script>



<svg style="display:none;">
	<defs>
		<path id="svg_pixeljam" d="M24,0L24,16L8,16L8,24L0,24L0,0L24,0ZM56,16L56,24L48,24L48,16L56,16ZM56,8L64,8L64,16L56,16L56,8ZM56,8L48,8L48,0L56,0L56,8ZM64,16L72,16L72,24L64,24L64,16ZM64,8L64,0L72,0L72,8L64,8ZM120,16L136,16L136,24L112,24L112,0L120,0L120,16ZM160,16L160,0L168,0L168,24L144,24L144,16L160,16ZM200,0L200,24L192,24L192,16L184,16L184,24L176,24L176,0L200,0ZM224,8L232,8L232,0L248,0L248,24L240,24L240,16L232,16L232,24L224,24L224,16L216,16L216,24L208,24L208,0L224,0L224,8ZM40,0L40,24L32,24L32,0L40,0ZM104,0L104,8L80,8L80,0L104,0ZM104,16L104,24L80,24L80,16L104,16Z" style="fill-rule:evenodd"/>
	</defs>
</svg>



<!-- press 'c' to open config -->
<!-- press 's' to toggle stats -->
<svelte:window
	on:keydown={(e) => {
		if(debug) {
			if(e.key == 'c') configOpen = !configOpen;
			if(e.key == 'p') chladniConfigOpen = !chladniConfigOpen;
			if(e.key == 's') system.stats.domElement.classList.toggle('hidden');
		}
	}}
/>


{#if await_user}
	<div class="flex flex-col screen">
		<Graphic viewbox="0 0 248 24" def="svg_pixeljam" css="pj-logo" />
		<Button color="purple" callback={() => { initV99() }}>Launch V99</Button>
	</div>
{:else}
	<div class="flex screen" bind:this={elem}></div>
	{#if debug}
<div class="config-chladni">
			<div class="inner" class:open={chladniConfigOpen}>
				{#if settings}
					<SectionHead title="Chladni" />
					<Slider min={-1} max={1} step={0.1} label="Chladni saturation" bind:value={settings.saturation} />
				{/if}
			</div>
		</div> <!-- config -->

		<div class="config">
			<!-- <Button solid={configOpen} callback={() => { configOpen = !configOpen }}>Config</Button> -->
			<div class="inner" class:open={configOpen}>
				{#if settings}
					<SectionHead title="Vector Shaders" />
					<Slider min={-1} max={1} step={0.1} label="Ysaturation" bind:value={settings.saturation} />
					<Slider min={0.5} max={2.5} step={0.01} label="exposure" bind:value={settings.exposure} />
					<Slider min={0} max={10} step={0.1} label="beam focus" bind:value={settings.beamFocus} />
					<Slider min={0} max={5} step={0.05} label="aberration" bind:value={settings.aberration} />
					<Slider min={0} max={5} step={0.05} label="astigmatism" bind:value={settings.astigmatism} />
					<Slider min={1} max={256} step={1} label="glow radius" bind:value={settings.glowRadius} />
					<Slider min={0} max={50} step={1} label="glow iterations" bind:value={settings.glowIterations} />
					<Slider min={0} max={1} step={0.01} label="camera noise" bind:value={settings.noise} />
					<Slider min={0} max={5} step={0.1} label="contrast" bind:value={settings.contrast} />
					<Slider min={0} max={1} step={1} label="antialiasing" bind:value={settings.antialiasing} />
					<SectionHead title="Vector Beam" />
					<Slider min={0} max={4} step={0.05} label="accuracy" bind:value={settings.accuracy} />
					<Slider min={0} max={1} step={0.01} label="voltage slop" bind:value={settings.voltageSlop} />
					<Slider min={0} max={1} step={0.01} label="tracer slop" bind:value={settings.tracers} />
					<Slider min={0.5} max={4} step={0.25} label="beam width" bind:value={settings.beamWidth} />
					<Slider min={0.1} max={40} step={0.1} label="lfo multiplier" bind:value={settings.lfoMult} />
					<SectionHead title="Engine" />
					<Slider min={0} max={1} step={1} label="viewport culling" bind:value={settings.culling} />
					<Slider min={20} max={120} step={10} label="max fps" bind:value={settings.maxFPS} />
					<Slider min={0.01} max={1} step={0.01} label="time warp" bind:value={settings.timewarp} />
					<!-- <SectionHead title="Audio" /> -->
					<!-- <Slider min={0} max={1} step={0.01} label="Voltage leak" bind:value={settings.fuzzGain} /> -->
					<!-- <Slider min={60} max={6000} step={10} label="fuzz highpass" bind:value={settings.fuzzFreq} /> -->
					<!-- <Slider min={0.1} max={2} step={0.1} label="fuzz pitch" bind:value={settings.fuzzPitch} /> -->
					<SectionHead title="Import / Export" />
					<div class="io">
						<Button
							callback={() => {
								dump = JSON.stringify(system.display.settings);
							}}
						>
							Export
						</Button>
						&nbsp;
						<Button
							disabled={dump == ''}
							callback={() => {
								dumpElem.select();
								document.execCommand('copy');
								dump = '';
								dumpElem.blur();
							}}
						>
							Copy
						</Button>
						&nbsp;
						<textarea bind:this={dumpElem} bind:value={dump}></textarea>
						&nbsp;
						<Button
							disabled={dump == ''}
							callback={() => {
								system.display.initShaders(JSON.parse(dump));
							}}
						>
							Import
						</Button>
					</div>
				{/if}
			</div>
		</div>
	{/if}
{/if}



<style>
	.io {
		display:flex;
		width:100%;
		flex-wrap:wrap;
		flex-direction:row;
		margin-top:0.5rem;
	}
	textarea {
		font-size:6px;
		font-family:monospace;
		color:var(--white);
		background:var(--black);
		width:9rem;
		resize:none;
	}
	:global(.pj-logo) {
		width:16rem;
		margin:2rem 0 1.4rem 0;
		color:var(--purple);
	}
	.screen {
		background:#000000;
	}
	:global(.screen canvas) {
		cursor:none !important;
	}
	.config {
		position: fixed;
		top:0;
		left:0;
		z-index:1000000;
		padding:0.5rem;
		font-size:0.6rem;
		width:100%;
		max-width:30em;
		pointer-events:none;
	}

	.config-chladni {
		position: fixed;
		top:0;
		right:0;
		z-index:1000000;
		padding:0.5rem;
		font-size:0.6rem;
		width:100%;
		max-width:30em;
		pointer-events:none;
	}

	.inner {
		display:none;
		background:rgba(0,0,0,0.5);
		padding-bottom:0.5em;
		pointer-events:auto;
	}
	.inner.open {
		text-transform:uppercase;
		width:100%;
		padding-top:0.5rem;
		display:flex;
		flex-direction:column;
		align-items: start;
		justify-content: start;
	}
</style>